/* tslint:disable */
/* eslint-disable */
/**
 * UpContent Platform API
 * # Introduction  The UpContent Public API provides access to UpContent's powerful content curation platform, allowing developers to build powerful content discovery and curation features directly into their applications.  # Authentication  The UpContent Public API uses OAuth2 authorization to grant applications access to UpContent user resources. A client application may request access to a single UpContent account or allow users to authorize access by that client to their UpContent resources.  ## User Authorization  UpContent uses a compliant OAuth2 flow to allow users to authorize access to their resources from third-party applications.  This implies that the access token must be requested from an authenticated client that can maintain the privacy of its credentials. In practice, this means that the access token must be requested from a server, rather than a JavaScript or mobile application.   ### Choosing a Grant Type The `authorization_code` is for applications intended to be use by multiple accounts where an user will grant access to their UpContent account to your application.  The `client_credentials` grant is designed for easy access to your own account without need for a full OAuth2 user authorization grant flow.  ### Choosing a Valid Scope  The UpContent API provides authorization scopes for read and write access based on the type of authorization grant requested. A valid scope must be chosen from the following:  Scope          | Grants                                     | Description ---------------|--------------------------------------------|------------ `client.read`  | `client_credentials`, `authorization_code` | Provides access to most public GET methods outlined in the documentation. `client.write` | `client_credentials`, `authorization_code` | Provides access to most public PUT, POST, and DElETE methods outlined in the documentation. `client.all`   | `client_credentials`, `authorization_code` | Alias for `client.read client.write`. `{module}.read`  | `authorization_code`                       | Provides access to a specific module's public GET methods outlined in the documentation. `{module}.write` | `authorization_code`                       | Provides access to a specific module's public PUT, POST, and DElETE methods outlined in the documentation. `{module}.all`   | `authorization_code`                       | Alias for `{module}.read {module}.write`.  Where `{module}` is one of the following: - team - folders - assignments - subscription - user - collections - collection_content - accounts - topics - topic_content - galleries - templates - quality  Currently module scopes are by special request only. By default when your client credentials are created they are given permission to use client.all.   ### Request Authorization  Begin by redirecting the user to the UpContent OAuth2 authorization page via the following request, with the querystring specified:  ``` GET https://accounts.upcontent.com/authorize  client_id={client_id}& response_type=code& grant_type=authorization_code& state={state}& redirect_uri={redirect_url}& scope={scope} ```  Parameter      | Description ---------------|--------------------------------------------------------------------- `client_id`    | The client identifier assigned to your client by UpContent. `grant_type`   | The OAuth2 grant type that is being used (`authorization_code`). `response_type`| The OAuth2 response type that is being used (`code`) `state`        | A value generated by the client that will be returned to the client to ensure that the authorization code that is returned is associated with an access token request. `redirect_uri` | One of the URLs previously associated with your client during registration with UpContent. The authorization code will be sent back to this URL. `scope`        | A list of space-delimited permissions for which the client is requesting authorization.  An allowed value for the `scope` parameter must be chosen from the table in the section *Choosing a Valid Scope*.  Upon successful authorization, an authorization code will be returned to the given redirect URL with the `state` parameter appended to the querystring, in addition to a `code` parameter which contains the authorization code.  ### Request an Access Token  Once a client has received an authorization code, it may then request an access token and a refresh token. The access token's purpose is to authenticate the client and user to the UpContent API. The purpose of the refresh token is to retrieve a new access token without the need to request a new authorization code.  The following endpoint may be used to request an access token, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=authorization_code& code={code}& redirect_url={redirect_url}& client_id={client_id}& client_secret={client_secret} ```  Field           | Description ----------------|------------ `grant_type`    | The OAuth2 grant type used in the previous step (`authorization_code`) `code`          | The authorization code obtained in the previous step `redirect_uri`  | The redirect URL to which the code was sent `client_id`     | The client identifier assigned to your client by UpContent `client_secret` | The client secret assigned to your client by UpContent  If an access token is granted, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.   ## Client-only Authorization  The UpContent API also provides a `client_credentials` grant for clients which only need access to a private UpContent account. This grant authorizes a client and generates an access token simultaneously on provision of the credentials generated when the client is registered with UpContent.  The following endpoint may be used to request an access token with this grant type, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=client_credentials& client_id={client_id}& client_secret={client_secret}& scope={scope} ```  The fields described in the previous section have the same meaning here. The scope parameter here is optional, and if omitted will default to the value `client.all` (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a **200 response** and a **JSON encoded object** described in the *Access Token* section.  Use of the `client_credentials` grant type can only provide access tokens to the UpContent account of the client's owner.   ## Access Token  On a successful access token request, the UpContent API will return a **200 Response** and a **JSON-encoded object** with the following properties:  Property        | Type    | Description ----------------|---------|------------ `access_token`  | string  | The token used to access the UpContent API. `token_type`    | string  | The type of token. This will be 'bearer'. `expires_in`    | integer | The number of seconds until the token is expected to expire. `refresh_token` | string  | The token used to request a new access token.  The client will be responsible for securing the `access_token` and `refresh_token`, and for updating expired access tokens (see *Refreshing Access*). Note that from the date of issuance, the `access_token` will be valid for `expires_in` seconds and the `refresh_token` will be valid for 30 days.   ## Refreshing Access  Access tokens are generated with an expiration date, after which point they will no longer grant access to the UpContent API. When an access token expires, it can be refreshed using the `refresh_token` provided through the OAuth2 `refresh_token` grant.  This is accomplished making the following request, with parameters submitted as form data:  ``` POST https://accounts.upcontent.com/token  Content-Type: application/x-www-form-urlencoded  grant_type=refresh_token& refresh_token={refresh_token}& scope={scope} ```  The fields described in the previous section have the same meaning here; the field `refresh_token` should be set to the refresh token provided at the time the expired access token was provided. The scope parameter here is optional; if provided, it must be a equal to or a subset of the scope of the expired access token, and if omitted will default to the full scope of the expired token (see *Choosing a Valid Scope*). If the request is successful, the endpoint will return a new access and refresh token in the format defined above.  If both the access and refresh tokens for a user expire, the client must reobtain authorization from that user following the procedure described in *User Authorization*.   ## Accessing the UpContent API  Upon grant of an access token, it can be used until expiration to request data from or send data to the UpContent API endpoints with scopes for which the client is authorized. This is done by including the header ``` Authorization: Bearer {token} ``` in requests to the API, where {token} represents the access token. 
 *
 * OpenAPI spec version: 2.0.0
 * 
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */
import globalAxios, { AxiosResponse, AxiosInstance, AxiosRequestConfig } from 'axios';
import { Configuration } from '../configuration';
// Some imports not used depending on template conditions
// @ts-ignore
import { BASE_PATH, COLLECTION_FORMATS, RequestArgs, BaseAPI, RequiredError } from '../base';
import { AccountIdInvitationsBody } from '../models';
import { AccountIdUsersBody } from '../models';
import { InlineResponse20122 } from '../models';
import { InlineResponse20123 } from '../models';
import { InlineResponse20124 } from '../models';
import { InlineResponse2013 } from '../models';
import { Invitation } from '../models';
import { InvitationURL } from '../models';
import { User } from '../models';
import { UserIdPrivilegesBody } from '../models';
import { UserURL } from '../models';
import { WixEmailBody } from '../models';
import { WixNameBody } from '../models';
import { WixRefreshBody } from '../models';
import { WixSessionsBody } from '../models';
import { WixUser } from '../models';
import { WixUsersBody } from '../models';
/**
 * UsersApi - axios parameter creator
 * @export
 */
export const UsersApiAxiosParamCreator = function (configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get the list of open invitations to the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdInvitationsGet: async (account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdInvitationsGet.');
            }
            const localVarPath = `/accounts/{account_id}/invitations`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Send an invitation to join the account
         * @param {AccountIdInvitationsBody} body Data for creating an invitation and assigning folders on acceptance
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdInvitationsPost: async (body: AccountIdInvitationsBody, account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling accountsAccountIdInvitationsPost.');
            }
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdInvitationsPost.');
            }
            const localVarPath = `/accounts/{account_id}/invitations`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the list of users associated with the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdUsersGet: async (account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdUsersGet.');
            }
            const localVarPath = `/accounts/{account_id}/users`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Add an invited user to the account
         * @param {AccountIdUsersBody} body Data for accepting an invitation to join the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdUsersPost: async (body: AccountIdUsersBody, account_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling accountsAccountIdUsersPost.');
            }
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdUsersPost.');
            }
            const localVarPath = `/accounts/{account_id}/users`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Remove an associated user from the account
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdUsersUserIdDelete: async (account_id: string, user_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdUsersUserIdDelete.');
            }
            // verify required parameter 'user_id' is not null or undefined
            if (user_id === null || user_id === undefined) {
                throw new RequiredError('user_id','Required parameter user_id was null or undefined when calling accountsAccountIdUsersUserIdDelete.');
            }
            const localVarPath = `/accounts/{account_id}/users/{user_id}`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(user_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get a user associated with the account
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdUsersUserIdGet: async (account_id: string, user_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdUsersUserIdGet.');
            }
            // verify required parameter 'user_id' is not null or undefined
            if (user_id === null || user_id === undefined) {
                throw new RequiredError('user_id','Required parameter user_id was null or undefined when calling accountsAccountIdUsersUserIdGet.');
            }
            const localVarPath = `/accounts/{account_id}/users/{user_id}`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(user_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the account role granted to the user
         * @param {UserIdPrivilegesBody} body Data for changing a user&#x27;s account role
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        accountsAccountIdUsersUserIdPrivilegesPut: async (body: UserIdPrivilegesBody, account_id: string, user_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling accountsAccountIdUsersUserIdPrivilegesPut.');
            }
            // verify required parameter 'account_id' is not null or undefined
            if (account_id === null || account_id === undefined) {
                throw new RequiredError('account_id','Required parameter account_id was null or undefined when calling accountsAccountIdUsersUserIdPrivilegesPut.');
            }
            // verify required parameter 'user_id' is not null or undefined
            if (user_id === null || user_id === undefined) {
                throw new RequiredError('user_id','Required parameter user_id was null or undefined when calling accountsAccountIdUsersUserIdPrivilegesPut.');
            }
            const localVarPath = `/accounts/{account_id}/users/{user_id}/privileges`
                .replace(`{${"account_id"}}`, encodeURIComponent(String(account_id)))
                .replace(`{${"user_id"}}`, encodeURIComponent(String(user_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Cancel an account invitation
         * @param {string} invitation_id UUID for the invitation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        invitationsInvitationIdDelete: async (invitation_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitation_id' is not null or undefined
            if (invitation_id === null || invitation_id === undefined) {
                throw new RequiredError('invitation_id','Required parameter invitation_id was null or undefined when calling invitationsInvitationIdDelete.');
            }
            const localVarPath = `/invitations/{invitation_id}`
                .replace(`{${"invitation_id"}}`, encodeURIComponent(String(invitation_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'DELETE', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get an account invitation
         * @param {string} invitation_id UUID for the invitation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        invitationsInvitationIdGet: async (invitation_id: string, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'invitation_id' is not null or undefined
            if (invitation_id === null || invitation_id === undefined) {
                throw new RequiredError('invitation_id','Required parameter invitation_id was null or undefined when calling invitationsInvitationIdGet.');
            }
            const localVarPath = `/invitations/{invitation_id}`
                .replace(`{${"invitation_id"}}`, encodeURIComponent(String(invitation_id)));
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Set the email address of a Wix user
         * @param {WixEmailBody} body Wix-provided user email address
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixEmailPut: async (body: WixEmailBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixEmailPut.');
            }
            const localVarPath = `/wix/email`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Set the name of a Wix user
         * @param {WixNameBody} body Wix-provided user name
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixNamePut: async (body: WixNameBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixNamePut.');
            }
            const localVarPath = `/wix/name`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Get the current Wix user's profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixProfileGet: async (options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            const localVarPath = `/wix/profile`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'GET', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Update the category refresh rate
         * @param {WixRefreshBody} body New category refresh rate type
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixRefreshPut: async (body: WixRefreshBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixRefreshPut.');
            }
            const localVarPath = `/wix/refresh`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'PUT', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Authenticate a wix user
         * @param {WixSessionsBody} body Wix authentication token
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixSessionsPost: async (body: WixSessionsBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixSessionsPost.');
            }
            const localVarPath = `/wix/sessions`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
        /**
         * 
         * @summary Create and authenticate a wix user
         * @param {WixUsersBody} body Wix user data
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        wixUsersPost: async (body: WixUsersBody, options: AxiosRequestConfig = {}): Promise<RequestArgs> => {
            // verify required parameter 'body' is not null or undefined
            if (body === null || body === undefined) {
                throw new RequiredError('body','Required parameter body was null or undefined when calling wixUsersPost.');
            }
            const localVarPath = `/wix/users`;
            // use dummy base URL string because the URL constructor only accepts absolute URLs.
            const localVarUrlObj = new URL(localVarPath, 'https://example.com');
            let baseOptions;
            if (configuration) {
                baseOptions = configuration.baseOptions;
            }
            const localVarRequestOptions :AxiosRequestConfig = { method: 'POST', ...baseOptions, ...options};
            const localVarHeaderParameter = {} as any;
            const localVarQueryParameter = {} as any;

            // authentication oauth2 required
            // oauth required
            if (configuration && configuration.accessToken) {
                const localVarAccessTokenValue = typeof configuration.accessToken === 'function'
                    ? await configuration.accessToken("oauth2", ["upcontent.read", "upcontent.write", "wix.read", "wix.write", "wix.admin", "client.read", "client.write", "client.all", "galleries.read", "galleries.write", "public"])
                    : await configuration.accessToken;
                localVarHeaderParameter["Authorization"] = "Bearer " + localVarAccessTokenValue;
            }

            localVarHeaderParameter['Content-Type'] = '*/*';

            const query = new URLSearchParams(localVarUrlObj.search);
            for (const key in localVarQueryParameter) {
                query.set(key, localVarQueryParameter[key]);
            }
            for (const key in options.params) {
                query.set(key, options.params[key]);
            }
            localVarUrlObj.search = (new URLSearchParams(query)).toString();
            let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
            localVarRequestOptions.headers = {...localVarHeaderParameter, ...headersFromBaseOptions, ...options.headers};
            const needsSerialization = (typeof body !== "string") || localVarRequestOptions.headers['Content-Type'] === 'application/json';
            localVarRequestOptions.data =  needsSerialization ? JSON.stringify(body !== undefined ? body : {}) : (body || "");

            return {
                url: localVarUrlObj.pathname + localVarUrlObj.search + localVarUrlObj.hash,
                options: localVarRequestOptions,
            };
        },
    }
};

/**
 * UsersApi - functional programming interface
 * @export
 */
export const UsersApiFp = function(configuration?: Configuration) {
    return {
        /**
         * 
         * @summary Get the list of open invitations to the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdInvitationsGet(account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<InvitationURL>>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).accountsAccountIdInvitationsGet(account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Send an invitation to join the account
         * @param {AccountIdInvitationsBody} body Data for creating an invitation and assigning folders on acceptance
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdInvitationsPost(body: AccountIdInvitationsBody, account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Invitation>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).accountsAccountIdInvitationsPost(body, account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the list of users associated with the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersGet(account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Array<UserURL>>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).accountsAccountIdUsersGet(account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Add an invited user to the account
         * @param {AccountIdUsersBody} body Data for accepting an invitation to join the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersPost(body: AccountIdUsersBody, account_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse2013>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).accountsAccountIdUsersPost(body, account_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Remove an associated user from the account
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersUserIdDelete(account_id: string, user_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).accountsAccountIdUsersUserIdDelete(account_id, user_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get a user associated with the account
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersUserIdGet(account_id: string, user_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<User>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).accountsAccountIdUsersUserIdGet(account_id, user_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the account role granted to the user
         * @param {UserIdPrivilegesBody} body Data for changing a user&#x27;s account role
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersUserIdPrivilegesPut(body: UserIdPrivilegesBody, account_id: string, user_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<UserIdPrivilegesBody>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).accountsAccountIdUsersUserIdPrivilegesPut(body, account_id, user_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Cancel an account invitation
         * @param {string} invitation_id UUID for the invitation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async invitationsInvitationIdDelete(invitation_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<void>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).invitationsInvitationIdDelete(invitation_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get an account invitation
         * @param {string} invitation_id UUID for the invitation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async invitationsInvitationIdGet(invitation_id: string, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<Invitation>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).invitationsInvitationIdGet(invitation_id, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Set the email address of a Wix user
         * @param {WixEmailBody} body Wix-provided user email address
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixEmailPut(body: WixEmailBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20123>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).wixEmailPut(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Set the name of a Wix user
         * @param {WixNameBody} body Wix-provided user name
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixNamePut(body: WixNameBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20122>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).wixNamePut(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Get the current Wix user's profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixProfileGet(options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<WixUser>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).wixProfileGet(options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Update the category refresh rate
         * @param {WixRefreshBody} body New category refresh rate type
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixRefreshPut(body: WixRefreshBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<InlineResponse20124>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).wixRefreshPut(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Authenticate a wix user
         * @param {WixSessionsBody} body Wix authentication token
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixSessionsPost(body: WixSessionsBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<string>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).wixSessionsPost(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
        /**
         * 
         * @summary Create and authenticate a wix user
         * @param {WixUsersBody} body Wix user data
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixUsersPost(body: WixUsersBody, options?: AxiosRequestConfig): Promise<(axios?: AxiosInstance, basePath?: string) => Promise<AxiosResponse<string>>> {
            const localVarAxiosArgs = await UsersApiAxiosParamCreator(configuration).wixUsersPost(body, options);
            return (axios: AxiosInstance = globalAxios, basePath: string = BASE_PATH) => {
                const axiosRequestArgs :AxiosRequestConfig = {...localVarAxiosArgs.options, url: basePath + localVarAxiosArgs.url};
                return axios.request(axiosRequestArgs);
            };
        },
    }
};

/**
 * UsersApi - factory interface
 * @export
 */
export const UsersApiFactory = function (configuration?: Configuration, basePath?: string, axios?: AxiosInstance) {
    return {
        /**
         * 
         * @summary Get the list of open invitations to the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdInvitationsGet(account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<InvitationURL>>> {
            return UsersApiFp(configuration).accountsAccountIdInvitationsGet(account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Send an invitation to join the account
         * @param {AccountIdInvitationsBody} body Data for creating an invitation and assigning folders on acceptance
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdInvitationsPost(body: AccountIdInvitationsBody, account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Invitation>> {
            return UsersApiFp(configuration).accountsAccountIdInvitationsPost(body, account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the list of users associated with the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersGet(account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Array<UserURL>>> {
            return UsersApiFp(configuration).accountsAccountIdUsersGet(account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Add an invited user to the account
         * @param {AccountIdUsersBody} body Data for accepting an invitation to join the account
         * @param {string} account_id UUID for the account
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersPost(body: AccountIdUsersBody, account_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse2013>> {
            return UsersApiFp(configuration).accountsAccountIdUsersPost(body, account_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Remove an associated user from the account
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersUserIdDelete(account_id: string, user_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return UsersApiFp(configuration).accountsAccountIdUsersUserIdDelete(account_id, user_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get a user associated with the account
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersUserIdGet(account_id: string, user_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<User>> {
            return UsersApiFp(configuration).accountsAccountIdUsersUserIdGet(account_id, user_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the account role granted to the user
         * @param {UserIdPrivilegesBody} body Data for changing a user&#x27;s account role
         * @param {string} account_id UUID for the account
         * @param {string} user_id UUID for the user
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async accountsAccountIdUsersUserIdPrivilegesPut(body: UserIdPrivilegesBody, account_id: string, user_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<UserIdPrivilegesBody>> {
            return UsersApiFp(configuration).accountsAccountIdUsersUserIdPrivilegesPut(body, account_id, user_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Cancel an account invitation
         * @param {string} invitation_id UUID for the invitation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async invitationsInvitationIdDelete(invitation_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<void>> {
            return UsersApiFp(configuration).invitationsInvitationIdDelete(invitation_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get an account invitation
         * @param {string} invitation_id UUID for the invitation
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async invitationsInvitationIdGet(invitation_id: string, options?: AxiosRequestConfig): Promise<AxiosResponse<Invitation>> {
            return UsersApiFp(configuration).invitationsInvitationIdGet(invitation_id, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Set the email address of a Wix user
         * @param {WixEmailBody} body Wix-provided user email address
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixEmailPut(body: WixEmailBody, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20123>> {
            return UsersApiFp(configuration).wixEmailPut(body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Set the name of a Wix user
         * @param {WixNameBody} body Wix-provided user name
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixNamePut(body: WixNameBody, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20122>> {
            return UsersApiFp(configuration).wixNamePut(body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Get the current Wix user's profile
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixProfileGet(options?: AxiosRequestConfig): Promise<AxiosResponse<WixUser>> {
            return UsersApiFp(configuration).wixProfileGet(options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Update the category refresh rate
         * @param {WixRefreshBody} body New category refresh rate type
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixRefreshPut(body: WixRefreshBody, options?: AxiosRequestConfig): Promise<AxiosResponse<InlineResponse20124>> {
            return UsersApiFp(configuration).wixRefreshPut(body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Authenticate a wix user
         * @param {WixSessionsBody} body Wix authentication token
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixSessionsPost(body: WixSessionsBody, options?: AxiosRequestConfig): Promise<AxiosResponse<string>> {
            return UsersApiFp(configuration).wixSessionsPost(body, options).then((request) => request(axios, basePath));
        },
        /**
         * 
         * @summary Create and authenticate a wix user
         * @param {WixUsersBody} body Wix user data
         * @param {*} [options] Override http request option.
         * @throws {RequiredError}
         */
        async wixUsersPost(body: WixUsersBody, options?: AxiosRequestConfig): Promise<AxiosResponse<string>> {
            return UsersApiFp(configuration).wixUsersPost(body, options).then((request) => request(axios, basePath));
        },
    };
};

/**
 * UsersApi - object-oriented interface
 * @export
 * @class UsersApi
 * @extends {BaseAPI}
 */
export class UsersApi extends BaseAPI {
    /**
     * 
     * @summary Get the list of open invitations to the account
     * @param {string} account_id UUID for the account
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async accountsAccountIdInvitationsGet(account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<InvitationURL>>> {
        return UsersApiFp(this.configuration).accountsAccountIdInvitationsGet(account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Send an invitation to join the account
     * @param {AccountIdInvitationsBody} body Data for creating an invitation and assigning folders on acceptance
     * @param {string} account_id UUID for the account
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async accountsAccountIdInvitationsPost(body: AccountIdInvitationsBody, account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Invitation>> {
        return UsersApiFp(this.configuration).accountsAccountIdInvitationsPost(body, account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the list of users associated with the account
     * @param {string} account_id UUID for the account
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async accountsAccountIdUsersGet(account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Array<UserURL>>> {
        return UsersApiFp(this.configuration).accountsAccountIdUsersGet(account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Add an invited user to the account
     * @param {AccountIdUsersBody} body Data for accepting an invitation to join the account
     * @param {string} account_id UUID for the account
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async accountsAccountIdUsersPost(body: AccountIdUsersBody, account_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse2013>> {
        return UsersApiFp(this.configuration).accountsAccountIdUsersPost(body, account_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Remove an associated user from the account
     * @param {string} account_id UUID for the account
     * @param {string} user_id UUID for the user
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async accountsAccountIdUsersUserIdDelete(account_id: string, user_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return UsersApiFp(this.configuration).accountsAccountIdUsersUserIdDelete(account_id, user_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get a user associated with the account
     * @param {string} account_id UUID for the account
     * @param {string} user_id UUID for the user
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async accountsAccountIdUsersUserIdGet(account_id: string, user_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<User>> {
        return UsersApiFp(this.configuration).accountsAccountIdUsersUserIdGet(account_id, user_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the account role granted to the user
     * @param {UserIdPrivilegesBody} body Data for changing a user&#x27;s account role
     * @param {string} account_id UUID for the account
     * @param {string} user_id UUID for the user
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async accountsAccountIdUsersUserIdPrivilegesPut(body: UserIdPrivilegesBody, account_id: string, user_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<UserIdPrivilegesBody>> {
        return UsersApiFp(this.configuration).accountsAccountIdUsersUserIdPrivilegesPut(body, account_id, user_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Cancel an account invitation
     * @param {string} invitation_id UUID for the invitation
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async invitationsInvitationIdDelete(invitation_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<void>> {
        return UsersApiFp(this.configuration).invitationsInvitationIdDelete(invitation_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get an account invitation
     * @param {string} invitation_id UUID for the invitation
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async invitationsInvitationIdGet(invitation_id: string, options?: AxiosRequestConfig) : Promise<AxiosResponse<Invitation>> {
        return UsersApiFp(this.configuration).invitationsInvitationIdGet(invitation_id, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Set the email address of a Wix user
     * @param {WixEmailBody} body Wix-provided user email address
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async wixEmailPut(body: WixEmailBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20123>> {
        return UsersApiFp(this.configuration).wixEmailPut(body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Set the name of a Wix user
     * @param {WixNameBody} body Wix-provided user name
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async wixNamePut(body: WixNameBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20122>> {
        return UsersApiFp(this.configuration).wixNamePut(body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Get the current Wix user's profile
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async wixProfileGet(options?: AxiosRequestConfig) : Promise<AxiosResponse<WixUser>> {
        return UsersApiFp(this.configuration).wixProfileGet(options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Update the category refresh rate
     * @param {WixRefreshBody} body New category refresh rate type
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async wixRefreshPut(body: WixRefreshBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<InlineResponse20124>> {
        return UsersApiFp(this.configuration).wixRefreshPut(body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Authenticate a wix user
     * @param {WixSessionsBody} body Wix authentication token
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async wixSessionsPost(body: WixSessionsBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<string>> {
        return UsersApiFp(this.configuration).wixSessionsPost(body, options).then((request) => request(this.axios, this.basePath));
    }
    /**
     * 
     * @summary Create and authenticate a wix user
     * @param {WixUsersBody} body Wix user data
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     * @memberof UsersApi
     */
    public async wixUsersPost(body: WixUsersBody, options?: AxiosRequestConfig) : Promise<AxiosResponse<string>> {
        return UsersApiFp(this.configuration).wixUsersPost(body, options).then((request) => request(this.axios, this.basePath));
    }
}
